import { useMemo } from 'react';

import { useAtomValue } from 'jotai';

import { ParameterGroupTable } from '@blocks';
import { useTranslate } from '@i18n';
import {
    useCategories,
    useUiNodes,
    useCorridors,
    useActivityReport,
    useActivitySwings,
    useUiParameters,
    useUiGraph,
} from '@queries';
import { activityIdAtom, activityUnitAtom } from '@store';
import { NavigationMenu, NavigationMenuItem, Heading2 } from '@ui';

import { ActivityReportGraphs } from './components/ActivityReportGraph';
import { ActivityReportLayout } from './components/ActivityReportLayout/ActivityReportLayout';
import { useActiveLayout } from './hooks/useActiveLayout';
import { useActivityReportSearchParams, GRAPH_PARAM_KEY, GROUP_PARAM_KEY } from './hooks/useActivityReportSearchParams';
import { useGraphNodes } from './hooks/useGraphNodes';
import { useParameterNodes } from './hooks/useParameterNodes';

type ActivityReportProps = {
    getLinkBack: () => string;
};

export const ActivityReport = ({ getLinkBack }: ActivityReportProps) => {
    const activityId = useAtomValue(activityIdAtom);
    const activeLayout = useActiveLayout();

    const { data: swings } = useActivitySwings(activityId);
    const { data: activityReport } = useActivityReport(activityId);
    const activityUnit = useAtomValue(activityUnitAtom);
    const { data: corridor } = useCorridors(activityUnit);

    const { data: categories } = useCategories('swing_foundation_group');
    const { data: parameterNodes } = useUiParameters();
    const { data: swingFoundationNodes } = useUiNodes('swing_foundations');

    const { data: graphCategories } = useCategories('graph_type');
    const { data: graphNodes } = useUiGraph();

    const { allUsersGraphNodesIds, allUsersParameters } = useMemo(() => {
        const allUsersUiNodes = activeLayout?.customizations.filter((d) => d.showChildOn?.includes('kiosk'));
        const allUsersGraphNodesIds = new Set(
            allUsersUiNodes?.filter((d) => d.childUiNodeId.split('.')[0] === 'graph').map((d) => d.childUiNodeId),
        );
        const allUsersParameters = new Set(
            allUsersUiNodes
                ?.filter(
                    (node) =>
                        (node.parentUiNodeId && node.parentUiNodeId.indexOf('module.custom_') !== -1) ||
                        node.parentUiNodeId === 'swing_foundations.default_kiosk',
                )
                .map((d) => d.childUiNodeId),
        );

        return {
            allUsersParameters,
            allUsersGraphNodesIds,
        };
    }, [activeLayout?.customizations]);

    const { graphMap, graphNavigationLinks } = useGraphNodes({
        graphCategories,
        graphNodes,
        usersGraphNodes: allUsersGraphNodesIds,
    });

    const isLoading = !parameterNodes || !swingFoundationNodes || !categories || !activityReport;

    const { swingFoundationPositionNodes, swingFoundationParameterCategories, groupParameterMap } = useParameterNodes({
        categories,
        swingFoundationNodes,
        parameterNodes,
        allUsersParameters,
    });

    const [selectedGroup, selectedGraph, setActivityReportSearchParams] = useActivityReportSearchParams();

    const currentGroup = selectedGroup ? swingFoundationPositionNodes[selectedGroup] : null;
    const hasGraph = !!(graphNavigationLinks && Object.entries(graphNavigationLinks).length);

    const t = useTranslate();
    return (
        <ActivityReportLayout
            isLoading={isLoading}
            backLink={getLinkBack}
            sidebar={
                <>
                    <NavigationMenu title={t('activity_report.swing_foundations')}>
                        {Object.entries(swingFoundationPositionNodes).map(([key, group]) => (
                            <NavigationMenuItem
                                key={key}
                                onClick={() => setActivityReportSearchParams(GROUP_PARAM_KEY, key)}
                                active={selectedGroup === key}
                            >
                                {t(group?.i18n ?? '-')}
                            </NavigationMenuItem>
                        ))}
                    </NavigationMenu>
                    {hasGraph && (
                        <NavigationMenu title={t('activity_report.graphs')}>
                            {Object.entries(graphNavigationLinks).map(([key, value]) => (
                                <NavigationMenuItem
                                    key={key}
                                    onClick={() => setActivityReportSearchParams(GRAPH_PARAM_KEY, key)}
                                    active={selectedGraph === key}
                                >
                                    {value}
                                </NavigationMenuItem>
                            ))}
                        </NavigationMenu>
                    )}
                </>
            }
            content={
                <>
                    {selectedGroup && (
                        <>
                            <Heading2>{t(currentGroup?.i18n ?? '-')}</Heading2>
                            {swingFoundationParameterCategories?.[selectedGroup]?.map((group) => {
                                const parameters = groupParameterMap?.[group.id];
                                return parameters ? (
                                    <ParameterGroupTable
                                        userPreferredUnit={activityUnit}
                                        key={group.id}
                                        title={t(group.i18n ?? '-')}
                                        activityReport={activityReport}
                                        parameters={parameters}
                                        corridor={corridor}
                                    />
                                ) : null;
                            })}
                        </>
                    )}
                    {selectedGraph && <ActivityReportGraphs swings={swings} graphNodes={graphMap?.[selectedGraph]} />}
                </>
            }
        />
    );
};
